<template>
  <div>
    <div
      class="infusion-function-check el-input-group el-input-group--append el-input-group--prepend"
    >
      <el-select
        v-close-on-zoom
        :value="state.check.target"
        :disabled="readonly"
        class="target-select"
        @input="value => updateTarget(value)"
      >
        <el-option value="ANY" :label="'Was ever'" />
        <el-option value="CURRENT" label="Is most recent" />
      </el-select>
      <case-logic-label>infusable</case-logic-label>
      <medication-fluid-selector
        :selected="state.check.id"
        :case-conf="emitter.caseConf"
        :medications="medications"
        :disabled="readonly"
        :is-global="isGlobal"
        :dosage-form-filter="
          dosageForm =>
            dosageForm.enabledRoutes.find(r => r.key === 'IV_IO_INFUSION')
        "
        @select="
          (treatmentType, treatmentId) => {
            if (treatmentType === 'MEDICATION') {
              selectMedication(treatmentId);
            } else if (treatmentType === 'FLUID') {
              selectFluid(treatmentId);
            }
          }
        "
      />
      <help-tooltip v-if="medFluidError" type="error" class="error-tooltip">
        <span>{{ medFluidError.message }}</span>
      </help-tooltip>
      <help-tooltip v-if="ivDisabledError" type="error">
        <span>{{ ivDisabledError.message }}</span>
      </help-tooltip>
      <case-logic-label>was adjusted</case-logic-label>
      <el-select
        v-close-on-zoom
        placeholder="Select adjustment"
        class="adjustment-select"
        :disabled="readonly"
        :value="state.check.value"
        @input="id => selectFunction(id)"
      >
        <el-option
          v-for="value in functions"
          :key="value.key"
          :label="value.label.en"
          :value="value.key"
        />
      </el-select>
      <help-tooltip v-if="adjustmentDisabledError" type="error">
        <span>{{ adjustmentDisabledError.message }}</span>
      </help-tooltip>
    </div>

    <el-card v-if="state.check.id" class="infusion-location-options-container">
      <el-select
        v-close-on-zoom
        :value="state.check.ivInfusionLocation"
        :disabled="readonly"
        placeholder="Any infusion location"
        class="iv-infusion-location-select"
        @input="value => updateIvInfusionLocation(value)"
      >
        <el-option value="IV_SITE" label="IV Site" />
        <el-option value="MEDICATION" label="Medication" />
        <el-option value="FLUID" label="Fluid" />
      </el-select>
      <iv-location-select
        v-if="state.check.ivInfusionLocation === 'IV_SITE'"
        :iv-location="state.check.ivInfusionLocationIv || {}"
        :case-conf="emitter.caseConf"
        :is-global="isGlobal"
        :readonly="readonly"
        :iv-category-error="ivCategoryError"
        :iv-port-error="ivPortError"
        :iv-site-error="ivSiteError"
        :iv-side-error="ivSideError"
        @update="updateIvInfusionLocationIv"
      />

      <medication-fluid-selector
        v-if="state.check.ivInfusionLocation === 'MEDICATION'"
        :multiple="true"
        :show-fluids="false"
        :selected="state.check.ivInfusionLocationMedications || []"
        :case-conf="emitter.caseConf"
        :medications="medications"
        :disabled="readonly"
        :dosage-form-filter="
          dosageForm =>
            dosageForm.enabledRoutes.find(r => r.key === 'IV_IO_INFUSION')
        "
        :is-global="isGlobal"
        @select="
          (treatmentType, treatmentIds) => {
            updateIvInfusionLocationMedications(treatmentIds);
          }
        "
      />
      <help-tooltip v-if="locationMedError" type="error">
        <span>{{ locationMedError.message }}</span>
      </help-tooltip>

      <medication-fluid-selector
        v-if="state.check.ivInfusionLocation === 'FLUID'"
        :multiple="true"
        :show-meds="false"
        :selected="state.check.ivInfusionLocationFluids || []"
        :case-conf="emitter.caseConf"
        :medications="medications"
        :disabled="readonly"
        :is-global="isGlobal"
        @select="
          (treatmentType, treatmentIds) => {
            updateIvInfusionLocationFluids(treatmentIds);
          }
        "
      />
      <help-tooltip v-if="locationFluidError" type="error">
        <span>{{ locationFluidError.message }}</span>
      </help-tooltip>
    </el-card>
  </div>
</template>

<script>
import IvLocationSelect from './common/IVLocationSelect.vue';
import CaseLogicLabel from './common/CaseLogicLabel.vue';
import HelpTooltip from '~lib/common/components/HelpTooltip';
import MedicationFluidSelector from './common/MedicationFluidSelector';
import infusionCheckMixins from './common/InfusionCheckMixins';

export default {
  name: 'infusion-function-check',
  components: {
    HelpTooltip,
    MedicationFluidSelector,
    IvLocationSelect,
    CaseLogicLabel,
  },
  mixins: [ infusionCheckMixins ],
  props: ['medications', 'readonly', 'emitter', 'ikey', 'getData', 'putData', 'control'],
  data() {
    const check = this.getData(this.ikey);
    return {
      state: {
        check: check || {
          id: null,
          target: 'ANY',
          value: 'CHANGED',
          ivInfusionLocation: null,
          ivInfusionLocationIv: null,
          ivInfusionLocationMedications: null,
          ivInfusionLocationFluids: null,
        },
        selectedType:
          !check || check.type === 'INFUSION_MEDICATION_ACTION_TYPE'
            ? 'MEDICATION'
            : 'FLUID',
      },
      allFunctions: [
        { key: 'CHANGED', label: { en: 'Titrated' } },
        { key: 'PAUSED', label: { en: 'Paused' }, isCurrent: true },
        { key: 'RESUMED', label: { en: 'Resumed' } },
        { key: 'CLAMP_FULL_PAUSED', label: { en: 'Clamped Main Tubing' }, isCurrent: true },
        { key: 'CLAMP_FULL_RESUMED', label: { en: 'Unclamped Main Tubing' } },
        { key: 'DISCONNECTED', label: { en: 'Disconnected' } },
        { key: 'SYS_FINISHED', label: { en: 'Finished' } },
      ],
    };
  },
  computed: {
    isGlobal() {
      return this.emitter.isGlobal;
    },
    functions() {
      return this.allFunctions.filter(
        fun => this.state.check.target === 'ANY' || (this.state.check.target === 'CURRENT' && fun.isCurrent)
      );
    },
    valueError() {
      return this.errors.find(error =>
        error.metadata.medicationId === undefined &&
        error.metadata.fluidId === undefined
      );
    }
  },
  beforeMount() {
    this.emitter.trigger('process', this.control);
  },
  methods: {
    updateTarget(target) {
      this.state.check.target = target;
      this.resetFunction();
      this.update();
    },
    selectMedication(medicationId) {
      this.state.check.id = medicationId;
      this.state.selectedType = 'MEDICATION';
      this.update();
    },
    selectFluid(fluidId) {
      this.state.check.id = fluidId;
      this.state.selectedType = 'FLUID';
      this.update();
    },
    selectFunction(value) {
      this.state.check.value = value;
      this.update();
    },
    resetFunction() {
      if (this.state.check.target === 'CURRENT' && this.state.check.value) {
        const functionObj = this.allFunctions.find(fun => fun.key === this.state.check.value);
        if (functionObj && !functionObj.isCurrent) {
          this.state.check.value = null;
        }
      }
    },
    updateIvInfusionLocation(location) {
      this.state.check.ivInfusionLocation = location;
      this.update();
    },
    updateIvInfusionLocationIv(ivLocation) {
      this.state.check.ivInfusionLocationIv = ivLocation;
      this.update();
    },
    updateIvInfusionLocationMedications(medicationIds) {
      this.state.check.ivInfusionLocationMedications = medicationIds;
      this.update();
    },
    updateIvInfusionLocationFluids(fluidIds) {
      this.state.check.ivInfusionLocationFluids = fluidIds;
      this.update();
    },
    update() {
      const updatedCheck = {
        ...this.state.check,
        type:
          this.state.selectedType === 'MEDICATION'
            ? 'INFUSION_MEDICATION_ACTION_TYPE'
            : 'INFUSION_FLUID_ACTION_TYPE',
      };
      this.putData(this.ikey, updatedCheck);
      this.emitter.trigger('process', this.control);
    },
  },
};
</script>

<style scoped>
.infusion-function-check {
  display: flex;
}

.target-select {
  width: 150px;
}

.inline-label {
  background-color: #FAFCFD;
  color: #97A9D4;
  padding: 0 10px;
  display: table-cell;
  vertical-align: middle;
}

.error-tooltip {
  margin-left: -6px;
}

.medication-select {
  min-width: 300px;
}

.el-card >>> .el-card__body {
  display: flex;
  align-items: stretch;
}

.iv-infusion-location-select {
  min-width: 100px;
}
</style>
